/**
 * Central API configuration for Streaming Economics.
 *
 * Base URL resolution (matches the sibling apps, e.g. location-intelligence):
 *   - prod: VITE_API_BASE_URL="https://prodapi.parrotanalytics.com" (from the
 *     CircleCI d360-fe-prod context) → "https://prodapi.parrotanalytics.com/streaming-economics"
 *   - local dev: VITE_API_BASE_URL empty → relative "/streaming-economics" → Vite proxy → Flask
 *
 * Platform vs. leaderboard split: the leaderboard calls (API_BASE) always
 * derive from VITE_API_BASE_URL so they keep flowing through the Vite proxy in
 * local dev. The D360 portfolio (platform) calls use PLATFORM_BASE, which reads
 * VITE_PLATFORM_BASE_URL first and FALLS BACK to VITE_API_BASE_URL when that
 * override is unset. This lets you point the leaderboard at local Flask (empty
 * VITE_API_BASE_URL → proxy) while the portfolio calls hit prod
 * (VITE_PLATFORM_BASE_URL=https://prodapi.parrotanalytics.com) for end-to-end
 * local testing. When VITE_PLATFORM_BASE_URL is unset, portfolio calls keep the
 * existing behaviour (VITE_API_BASE_URL).
 *
 * Auth: the app stores a platform session token at localStorage['auth_token']
 * (set by pages/Login.tsx and pages/sso/SsoCallback.tsx). Every request attaches
 * it as the `x-auth-token` header when present — the same header d360 and the
 * sibling products send. A 401 clears the session (see components/AuthGuard
 * logout()) and redirects to the login page.
 */
import { logout } from '@/components/AuthGuard';

/**
 * Platform host — portfolio endpoints live here, OUTSIDE /streaming-economics.
 * Prefers the optional VITE_PLATFORM_BASE_URL override; falls back to
 * VITE_API_BASE_URL (existing behaviour) when the override is unset.
 */
export const PLATFORM_BASE =
  import.meta.env.VITE_PLATFORM_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? '';

export const API_BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/streaming-economics`;

/** Prefixes a path with the API base. */
export function apiUrl(path: string): string {
  // path already starts with / (e.g. '/leaderboard')
  return `${API_BASE}${path}`;
}

/** The current platform session token, or null when signed out. */
export function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

/** Session token as a header object (empty when signed out). */
function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { 'x-auth-token': token } : {};
}

/**
 * On a 401 the session is dead: clear local auth state and bounce to login.
 * logout() clears storage and returns the login path; we navigate the whole
 * document so the redirect works from anywhere (hooks, query fns, event
 * handlers) without needing the router in scope.
 */
function handleUnauthorized(): void {
  const to = logout();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign(to);
  }
}

/** Simple typed GET helper — attaches `x-auth-token` and handles 401. */
export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { ...authHeaders() } });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw new Error(`Request failed: ${url}`);
  return res.json();
}

/** Typed POST helper — JSON body, `x-auth-token`, same 401 handling as getJson. */
export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    // Surface the backend's JSON {error} message when present (e.g. the
    // "too many portfolios ids" 400) so callers can react to it.
    const detail = await res
      .clone()
      .json()
      .then((d: unknown) =>
        d &&
        typeof d === 'object' &&
        'error' in d &&
        typeof (d as { error: unknown }).error === 'string'
          ? (d as { error: string }).error
          : undefined,
      )
      .catch(() => undefined);
    const err = new Error(detail ?? `Request failed: ${url}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json();
}
